// Staff-auth for the god-view ADMIN section (the Filament replacement).
//
// Distinct from the ops console's auth (src/lib/auth.ts), which stores the
// X-Ops-Secret. The Admin section talks to admin-svc, which issues a real
// STAFF JWT (scope:"admin") at POST /v1/admin/login. We store that JWT and
// the API client attaches it as `Authorization: Bearer <jwt>`.
//
// The surface (token/login/logout/isAuthenticated + a change listener) mirrors
// the ops auth so the gate component reads the same; the difference is this one
// holds a short-lived JWT obtained via a password login, not a shared secret.

const TOKEN_KEY = 'telride.admin.token';
const ADMIN_KEY = 'telride.admin.profile';
const PERMS_KEY = 'telride.admin.permissions';

export interface AdminProfile {
  id: number;
  email: string;
  name: string;
  role: string;
}

/** The permission strings admin-svc grants this staffer's role (the SAME set
 *  the server enforces — see internal/rbac). The god-view gates its UI off
 *  these, so the client can never offer an action the server would 403. */
export type AdminPermission =
  | 'dashboard.read'
  | 'customers.read'
  | 'drivers.read'
  | 'trips.read'
  | 'wallet.read'
  | 'fares.read'
  | 'audit.read'
  | 'complaints.read'
  | 'support.read'
  | 'incentives.read'
  | 'promos.read'
  | 'payments.read'
  | 'driver_tips.read' // rider→driver tips audit log (earnings-svc owns driver_tips)
  | 'payouts.read'
  | 'notifications.read'
  | 'cms.read'
  | 'boosts.read' // driver earnings boosts (incentives-svc owns driver_boosts)
  | 'booking_statuses.read' // booking_statuses lookup list (config)
  | 'docks.read' // Boats docks + dock-distance matrix (admin-svc owns docks/dock_distances)
  | 'expenses.read' // driver expense reports (earnings-svc owns driver_expenses; admin-svc reads)
  | 'documents.review'
  | 'wallet.adjust'
  | 'customers.block'
  | 'customers.manage' // edit a customer's identity — first/last/email (proxies auth-svc update-identity)
  | 'drivers.ban'
  | 'drivers.service_line' // set a driver's service-line classification (admin-svc owns driver_service_lines)
  | 'trips.cancel'
  | 'reserves.assign' // offer a held reserve trip to a driver (proxies trip-svc admin-assign)
  | 'fares.edit'
  | 'fares.manage' // edit per-category (per-trip-type) fare rates (admin-svc owns fare_rates)
  | 'staff.manage'
  | 'complaints.manage'
  | 'support.manage'
  | 'incentives.manage'
  | 'promos.manage'
  | 'payouts.kyc_verify'
  | 'cms.manage'
  | 'notifications.send'
  | 'booking_statuses.manage' // create/edit/delete booking-status labels
  | 'docks.manage' // create/edit Boats docks + dock-distance km
  | 'expenses.manage' // approve/reject driver expense reimbursements
  | 'safety.read' // SOS / safety incident queue (safety-svc)
  | 'safety.manage' // acknowledge + resolve safety incidents
  // Widened deliberately: admin-svc is the source of truth for the catalogue,
  // and a permission added there must not fail the portal's build before
  // someone remembers to extend this union. The literals above still give
  // autocomplete and catch typos in the common cases; anything newer is
  // accepted at runtime and described by lib/permissionCatalog.
  | (string & {});

type Listener = (authed: boolean) => void;
const listeners = new Set<Listener>();

/** The stored staff JWT, or ''. */
export function getAdminToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

/** The stored staff profile (set on login), or null. */
export function getAdminProfile(): AdminProfile | null {
  try {
    const raw = localStorage.getItem(ADMIN_KEY);
    return raw ? (JSON.parse(raw) as AdminProfile) : null;
  } catch {
    return null;
  }
}

/** The stored permission set for this staffer's role (empty if none stored). */
export function getAdminPermissions(): AdminPermission[] {
  try {
    const raw = localStorage.getItem(PERMS_KEY);
    return raw ? (JSON.parse(raw) as AdminPermission[]) : [];
  } catch {
    return [];
  }
}

/** True once a staff JWT is stored. */
export function isAdminAuthenticated(): boolean {
  return getAdminToken() !== '';
}

/** Whether the current staffer's role grants `perm`. The single gate the UI
 *  uses to show/hide/disable actions; mirrors the server's rbac.Allowed. */
export function hasAdminPermission(perm: AdminPermission): boolean {
  return getAdminPermissions().includes(perm);
}

/** Persist the JWT + profile (+ permissions) after a successful login, then
 *  notify listeners. */
export function setAdminSession(
  token: string,
  profile: AdminProfile,
  permissions: AdminPermission[] = [],
): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(profile));
    localStorage.setItem(PERMS_KEY, JSON.stringify(permissions));
  } catch {
    /* storage disabled — session simply won't persist across reloads */
  }
  emit();
}

/** Refresh just the stored permissions (e.g. from a /me re-fetch) without
 *  touching the token/profile. Notifies listeners so gated UI re-renders. */
export function setAdminPermissions(permissions: AdminPermission[]): void {
  try {
    localStorage.setItem(PERMS_KEY, JSON.stringify(permissions));
  } catch {
    /* noop */
  }
  emit();
}

/** Clear the staff session (logout, or on a 401 from admin-svc). */
export function adminLogout(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    localStorage.removeItem(PERMS_KEY);
  } catch {
    /* noop */
  }
  emit();
}

/** Subscribe to staff auth-state changes; returns an unsubscribe fn. */
export function onAdminAuthChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(): void {
  const authed = isAdminAuthenticated();
  listeners.forEach((l) => l(authed));
}
